import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Cookie, Settings2 } from "lucide-react";
import {
  COOKIE_CONSENT_UPDATED_EVENT,
  hasConsented,
  openCookieSettings,
  setConsentState,
} from "@/utils/cookieConsent";
import { CookieSettingsModal } from "./CookieSettingsModal";

export const CookieConsentBanner: React.FC = () => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    // Check whether user has already given/saved consent
    if (!hasConsented()) {
      setShowBanner(true);
    }

    const handleConsentUpdated = () => {
      setShowBanner(false);
      setIsModalOpen(false);
    };

    window.addEventListener(COOKIE_CONSENT_UPDATED_EVENT, handleConsentUpdated);
    return () => {
      window.removeEventListener(COOKIE_CONSENT_UPDATED_EVENT, handleConsentUpdated);
    };
  }, []);

  const handleAcceptAll = () => {
    setConsentState({
      analytics: true,
      marketing: true,
    });
    setShowBanner(false);
  };

  const handleRejectNonEssential = () => {
    setConsentState({
      analytics: false,
      marketing: false,
    });
    setShowBanner(false);
  };

  const handleOpenSettings = () => {
    setIsModalOpen(true);
    openCookieSettings();
  };

  return (
    <>
      {showBanner && (
        <div
          id="cookie-consent-banner"
          role="region"
          aria-label="Cookie consent banner"
          className="fixed bottom-0 left-0 right-0 z-[120] bg-[#0F172A]/95 backdrop-blur-md text-white border-t border-slate-700/80 shadow-[0_-8px_30px_rgba(0,0,0,0.3)] animate-in slide-in-from-bottom duration-300"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div className="flex items-start gap-3.5 max-w-4xl text-left">
              <div className="p-2 rounded-xl bg-white/10 text-[#A5B4FC] shrink-0 mt-0.5">
                <Cookie className="w-5 h-5" />
              </div>
              <p className="text-[13px] sm:text-[14px] font-normal text-slate-200 leading-relaxed">
                {t(
                  "cookie.bannerText",
                  "We use cookies and similar technologies to enhance your browsing experience, analyze site traffic, and personalize content. Essential cookies are necessary for the website to function properly, while others help us improve our services. You can accept all cookies, reject non-essential ones, or customize your preferences. For more details, please see our"
                )}{" "}
                <Link
                  to="/privacy-policy"
                  className="font-medium text-[#A5B4FC] hover:text-white underline underline-offset-2 transition-colors inline-flex items-center gap-0.5"
                >
                  {t("cookie.privacyPolicy", "Privacy Policy")}
                </Link>
                .
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2 sm:gap-2.5 shrink-0 w-full lg:w-auto justify-end">
              {/* Button 1: Cookie Settings */}
              <button
                type="button"
                onClick={handleOpenSettings}
                className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 px-3.5 py-2 sm:py-2.5 rounded-[12px] border border-slate-600 hover:border-slate-400 bg-slate-800/80 hover:bg-slate-700 text-slate-200 hover:text-white text-[13px] sm:text-[14px] font-medium transition-all cursor-pointer whitespace-nowrap"
              >
                <Settings2 className="w-4 h-4" />
                <span>{t("cookie.cookieSettings", "Cookie Settings")}</span>
              </button>

              {/* Button 2: Reject Non-Essential */}
              <button
                type="button"
                onClick={handleRejectNonEssential}
                className="flex-1 sm:flex-initial inline-flex items-center justify-center px-4 py-2 sm:py-2.5 rounded-[12px] border border-slate-600 hover:border-slate-400 bg-transparent hover:bg-white/5 text-slate-200 hover:text-white text-[13px] sm:text-[14px] font-medium transition-all cursor-pointer whitespace-nowrap"
              >
                {t("cookie.rejectNonEssential", "Reject Non-Essential")}
              </button>

              {/* Button 3: Accept All */}
              <button
                type="button"
                onClick={handleAcceptAll}
                className="w-full sm:w-auto inline-flex items-center justify-center px-5 py-2 sm:py-2.5 rounded-[12px] bg-[#574ADB] hover:bg-[#4338CA] text-white text-[13px] sm:text-[14px] font-semibold transition-all shadow-md shadow-indigo-500/25 cursor-pointer whitespace-nowrap"
              >
                {t("cookie.acceptAll", "Accept All")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global Cookie Settings Modal */}
      <CookieSettingsModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        onConsentSaved={() => setShowBanner(false)}
      />
    </>
  );
};
